import { Link, useLocation, useNavigate } from "react-router-dom";
import { X_URL } from "@/lib/links";
import { XIcon } from "@/components/XIcon";
import { useState, useEffect } from "react";
import { Menu, X, UserCircle2, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/about", label: "About" },
    { to: "/divisions", label: "Divisions" },
    { to: "/events", label: "Events" },
    { to: "/media", label: "Media" },
    { to: "/schools", label: "Schools" },
    { to: "/chapters", label: "Chapters" },
    { to: "/forum", label: "Forum" },
    { to: "/start-a-chapter", label: "Start a Chapter" },
  ];

  const isMember = user && (user.role === "member" || user.role === "chapter_leader");

  const doLogout = () => { logout(); setOpen(false); navigate("/"); };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
        scrolled || pathname !== "/" ? "bg-[#0A1128] border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/logo-emblem.png" alt="Patriot Power USA" className="h-12 sm:h-14 w-auto" />
          <span className="font-display text-xl sm:text-2xl text-white leading-none tracking-tight hidden xs:inline sm:inline">
            PATRIOT POWER <span className="text-[#D90429]">USA</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.12em] font-semibold text-white/80 hover:text-white transition-colors duration-200 whitespace-nowrap"
            >
              {l.label}
            </Link>
          ))}
          {isMember ? (
            <>
              <Link to="/account" data-testid="nav-account-link" className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-semibold text-white/80 hover:text-white transition-colors duration-200 whitespace-nowrap">
                <UserCircle2 className="w-4 h-4" /> Account
              </Link>
              <button onClick={doLogout} data-testid="nav-logout-button" className="text-white/50 hover:text-white transition-colors duration-200" title="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-signin-link" className="text-xs uppercase tracking-[0.12em] font-semibold text-white/80 hover:text-white transition-colors duration-200 whitespace-nowrap">
              Sign In
            </Link>
          )}
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-x-link"
            aria-label="Follow us on X"
            className="text-white/80 hover:text-white transition-colors duration-200"
          >
            <XIcon className="w-4 h-4" />
          </a>
        </nav>

        <div className="lg:hidden flex items-center">
          <button
            data-testid="mobile-menu-toggle"
            className="text-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A1128] border-t border-white/10 px-5 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-white/90 uppercase tracking-[0.15em] text-sm font-semibold">
              {l.label}
            </Link>
          ))}
          {isMember ? (
            <>
              <Link to="/account" onClick={() => setOpen(false)} className="text-white/90 uppercase tracking-[0.15em] text-sm font-semibold">My Account</Link>
              <button onClick={doLogout} className="text-left text-white/60 uppercase tracking-[0.15em] text-sm font-semibold">Log Out</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="text-white/90 uppercase tracking-[0.15em] text-sm font-semibold">Sign In</Link>
          )}
          <a href={X_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-x-link-mobile" onClick={() => setOpen(false)} className="text-white/90 uppercase tracking-[0.15em] text-sm font-semibold flex items-center gap-2"><XIcon className="w-4 h-4" /> Follow on X</a>
        </div>
      )}
    </header>
  );
};
